import './styles/DropdownNativeSelect.scss';

import { CaretDown } from '@phosphor-icons/react';
import {
  Fragment,
  memo,
  ReactNode,
  SelectHTMLAttributes,
  useEffect,
  useMemo,
} from 'react';

import { cn } from '../../../../helpers/classBuilders';
import { useClassHelper } from '../../../../hooks';
import { appTypes, ValueLabelType } from '../../../../types';
import { InputErrorMessage } from '../../../InputErrorMessage';
import { InputField } from '../../../InputField';

const variants = {
  default: '-default',
  'min-width': '-min-width',
} as const;

type OptionType = ValueLabelType & {
  id?: string;
};

interface Props extends SelectHTMLAttributes<HTMLSelectElement> {
  children?: ReactNode;
  error?: boolean;
  defaultValue?: string; // selects this value onMount
  errorMessage?: string;
  classes?: { container?: string } | null;
  variant?: keyof typeof variants;
  placeholder?: string;
  options?: OptionType[];
  label?: string | ReactNode;
  app?: keyof typeof appTypes;
  runOnChangeOnMount?: boolean;
  value?: string;
}

const componentName = 'c-DropdownNativeSelect';

export const DropdownNativeSelect = memo(
  ({
    error = undefined,
    errorMessage = undefined,
    children,
    classes = null,
    variant = 'default',
    placeholder,
    options,
    label,
    name,
    app = appTypes.portal,
    required,
    defaultValue = undefined,
    runOnChangeOnMount,
    value,
    ...selectProps
  }: Props) => {
    const c = useClassHelper(componentName);
    // Honour a caller-supplied id so the <label for> matches the <select id>
    // that `...selectProps` would otherwise override.
    const id = (selectProps as { id?: string }).id ?? `select_${name}`;
    const errorId = `${id}_ErrorMessage`;

    const errorClassNameReducer = useMemo(() => {
      const baseName = `${componentName}${variants[variant]}`;

      return {
        baseName,
        appName: `${componentName}-${app}${error ? '--error' : ''}`,
      };
    }, [error, app, variant]);

    /* On mount, if runOnChangeOnMount is true, trigger onChange with defaultValue */
    useEffect(() => {
      if (runOnChangeOnMount && defaultValue && defaultValue.length > 0) {
        selectProps.onChange({
          target: { name, value: defaultValue },
        } as any);
      }
    }, [runOnChangeOnMount]);

    const activeClassNames = [
      componentName,
      errorClassNameReducer.baseName,
      errorClassNameReducer.appName,
    ].join(' ');

    const { container: containerClassName } = classes || {};

    if (!options && !children) {
      return null;
    }

    return (
      <div className={containerClassName}>
        {typeof label === 'string' && (
          <InputField.Label id={id} required={required}>
            {label}
          </InputField.Label>
        )}
        {typeof label === 'object' && label}
        <div className={c('__container')}>
          <span className={c('__caret')} aria-hidden>
            <CaretDown size={16} />
          </span>
          <select
            aria-required={required}
            className={`${activeClassNames} ${cn(
              'border placeholder:text-gray-500 disabled:bg-(--color-input-background-disabled)! focus-visible:focus-ring-form',
              // The resting border carries ! — an SCSS rule can never outrank
              // it, so the error border must be the same kind of class. The
              // blue :focus/:active rule wins over both (also !important, one
              // specificity level up), keeping active above error.
              error
                ? 'border-(--color-input-border-error)!'
                : 'border-gray-300!',
            )}`}
            id={id}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? errorId : undefined}
            defaultValue={!value ? defaultValue : undefined}
            value={value}
            name={name}
            {...selectProps}
          >
            {options ? (
              <Fragment>
                {placeholder && (
                  <option value="" disabled>
                    {placeholder}
                  </option>
                )}
                {options.map(option => (
                  <option
                    key={option.value}
                    value={option.value}
                    id={option.id}
                  >
                    {option.label}
                  </option>
                ))}
              </Fragment>
            ) : (
              children
            )}
          </select>
        </div>
        <InputErrorMessage
          id={errorId}
          error={error}
          errorMessage={errorMessage}
          app={app}
        />
      </div>
    );
  },
);

DropdownNativeSelect.displayName = 'DropdownNativeSelect';
